/** The emailed signup code is always six numeric digits. */
export const CODE_LENGTH = 6;

const BLANK_ERROR = 'Verification code is required.';
const TOO_SHORT_ERROR = `Code must be at least ${CODE_LENGTH} characters.`;
const NON_NUMERIC_ERROR = `Code must be ${CODE_LENGTH} numeric digits.`;
const UNKNOWN_ERROR = 'Unknown error. Try again.';

const NUMERIC_CODE_REGEX = new RegExp(`^\\d{${CODE_LENGTH}}$`);

/** `null` when the code is submittable, otherwise the message to show. */
export function validateCodeInput(code: string): string | null {
  if (code.trim() === '') {
    return BLANK_ERROR;
  }
  if (code.length < CODE_LENGTH) {
    return TOO_SHORT_ERROR;
  }
  if (!NUMERIC_CODE_REGEX.test(code)) {
    return NON_NUMERIC_ERROR;
  }
  return null;
}

export type VerificationFailure = {
  message: string;
  /** Wrong-or-spent codes are cleared so the field is ready for a fresh one. */
  clearsCode: boolean;
};

/**
 * Maps a failed verification to its message. An expired code (400) is left in
 * the field so the visitor can see what they typed; a code the server no longer
 * recognises (404) or a rate-limited attempt (429) is cleared.
 */
export function resolveVerificationFailure(status: number | undefined): VerificationFailure {
  switch (status) {
    case 404:
      return { message: 'Request a new verification code and try again.', clearsCode: true };
    case 429:
      return { message: 'Too many attempts. Wait 15 minutes and try again.', clearsCode: true };
    case 400:
      return { message: 'Invalid or expired code.', clearsCode: false };
    default:
      return { message: UNKNOWN_ERROR, clearsCode: false };
  }
}

/** The resend endpoint only distinguishes its own rate limit. */
export function resolveResendFailure(status: number | undefined): VerificationFailure {
  if (status === 429) {
    return { message: 'Too many attempts. Wait 60 seconds and try again.', clearsCode: true };
  }
  return { message: UNKNOWN_ERROR, clearsCode: false };
}

export function invalidCodeMessage(email: string): string {
  return `Invalid code. Enter the verification code sent to ${email}.`;
}

export function codeResentMessage(email: string): string {
  return `A new code has been sent to ${email}.`;
}

/**
 * The join offer only stands with a token: the response marks both fields
 * optional and nullable, so normalize before testing.
 */
export function resolveJoinToken(result: {
  join_available?: boolean | null;
  join_token?: string | null;
}): string | null {
  const joinToken = result.join_token ?? '';
  if (result.join_available !== true || joinToken === '') {
    return null;
  }
  return joinToken;
}
